import { CloudAllowanceStatus, CloudWorkspace } from "@afar-research/contracts";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";

/**
 * How long what this browser last heard from AFAR Connect stays good enough to show without asking
 * again. Coming back to the page after this checks once; nothing checks while the page sits unused,
 * because every check reaches Connect's database and keeps it from pausing.
 */
export const CONNECT_SNAPSHOT_FRESH_MS = 5 * 60_000;

/** What this browser last heard from AFAR Connect about the signed-in account. */
export const ConnectSnapshot = Schema.Struct({
  accountKey: Schema.String,
  workspaces: Schema.Array(CloudWorkspace),
  allowance: Schema.NullOr(CloudAllowanceStatus),
  checkedAt: Schema.Number,
});
export type ConnectSnapshot = typeof ConnectSnapshot.Type;

const decodeStored = Schema.decodeUnknownOption(Schema.fromJsonString(ConnectSnapshot));
const encodeStored = Schema.encodeSync(Schema.fromJsonString(ConnectSnapshot));

/** A stored snapshot, or null when there is none or it no longer reads as one. */
export function readStoredConnectSnapshot(stored: string | null): ConnectSnapshot | null {
  return stored === null ? null : Option.getOrNull(decodeStored(stored));
}

export function storeConnectSnapshot(snapshot: ConnectSnapshot): string {
  return encodeStored(snapshot);
}

/**
 * Whether to ask Connect again. A snapshot belongs to one account, so another account's is no
 * answer at all; a forced check is the user asking, or a change this page just made.
 */
export function connectSnapshotNeedsCheck(input: {
  readonly snapshot: ConnectSnapshot | null;
  readonly accountKey: string;
  readonly now: number;
  readonly force: boolean;
  /** How old an answer may be and still do; a surface watching a change asks for a short one. */
  readonly freshMs?: number;
}): boolean {
  if (input.force || input.snapshot === null) return true;
  if (input.snapshot.accountKey !== input.accountKey) return true;
  return input.now - input.snapshot.checkedAt >= (input.freshMs ?? CONNECT_SNAPSHOT_FRESH_MS);
}
